"use client";
import { jsxs, jsx } from 'react/jsx-runtime';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import Attention from '../Attention/index.js';
import ButtonRow from '../ButtonRow/index.js';
import CloseButton from '../CloseButton/index.js';

const Sheet = ({
  centerHeader,
  children,
  className,
  footerButtonList,
  hasCloseButton,
  hasFooter,
  hasRequiredFieldsIndicator,
  onClose,
  subtitle,
  testSection,
  title,
  warningContent,
  warningTestSection,
  ...props
}) => {
  let subtitleContent;
  if (subtitle && typeof subtitle === "string") {
    subtitleContent = /* @__PURE__ */ jsx("p", { className: "push--top flush--bottom", children: subtitle });
  } else {
    subtitleContent = /* @__PURE__ */ jsx("div", { className: "push--top flush--bottom", children: subtitle });
  }
  let headerClasses = classNames({
    "oui-sheet__header": true,
    "text--center": centerHeader
  });
  return /* @__PURE__ */ jsxs("div", { className: classNames("oui-sheet__wrapper", className), ...props, children: [
    /* @__PURE__ */ jsx("div", { className: "oui-sheet__overlay" }),
    /* @__PURE__ */ jsxs(
      "div",
      {
        className: "oui-sheet overflow-y--auto",
        "data-oui-component": true,
        "data-test-section": testSection,
        children: [
          hasCloseButton && /* @__PURE__ */ jsx(CloseButton, { onClick: onClose, size: "large" }),
          warningContent && /* @__PURE__ */ jsx("div", { className: "oui-sheet__warning", children: /* @__PURE__ */ jsx(
            Attention,
            {
              alignment: "center",
              testSection: warningTestSection,
              type: "warning",
              children: warningContent
            }
          ) }),
          /* @__PURE__ */ jsxs("header", { className: headerClasses, children: [
            /* @__PURE__ */ jsx("h2", { className: "flush--bottom", children: title }),
            subtitleContent
          ] }),
          /* @__PURE__ */ jsx("div", { className: "oui-sheet__body", children }),
          hasFooter && /* @__PURE__ */ jsx("footer", { className: "oui-sheet__footer", children: /* @__PURE__ */ jsxs("div", { className: "flex flex-align--center", children: [
            hasRequiredFieldsIndicator && /* @__PURE__ */ jsx("div", { className: "oui-sheet__required-indicator cursor--default color--red", children: /* @__PURE__ */ jsx("span", { children: "* Required field" }) }),
            /* @__PURE__ */ jsx("div", { className: "flex--1", children: /* @__PURE__ */ jsx(ButtonRow, { rightGroup: footerButtonList }) })
          ] }) })
        ]
      }
    )
  ] });
};
Sheet.propTypes = {
  /**
   *  Used to determine if the title & subtitle of the sheet should be centered.
   */
  centerHeader: PropTypes.bool,
  /**
   *  The body of the sheet to request information and data from the user.
   */
  children: PropTypes.node.isRequired,
  /** CSS class names. */
  className: PropTypes.string,
  /**
   * Array of buttons used in the footer of the sheet.
   */
  footerButtonList: PropTypes.array,
  /**
   *  Used to determine if the sheet should have a close button.
   */
  hasCloseButton: PropTypes.bool,
  /**
   *  Determines if the sheet should have a footer.
   */
  hasFooter: PropTypes.bool,
  /**
   *  Determines if the sheet should have a message in the footer
   *  indicating required fields are marked with an asterisk (*).
   */
  hasRequiredFieldsIndicator: PropTypes.bool,
  /**
   * Function to perform when the sheet is closed.
   */
  onClose: PropTypes.func,
  /**
   * A subtitle for the sheet.
   */
  subtitle: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  /**
   * Identifier used to create data-test-section attributes for testing.
   */
  testSection: PropTypes.string,
  /**
   * Main title of the sheet.
   */
  title: PropTypes.string.isRequired,
  /**
   * Content of the warning Attention bar.
   */
  warningContent: PropTypes.string,
  /**
   * Identifier used to create data-test-section
   * attribute for the Attention bar.
   */
  warningTestSection: PropTypes.string
};
Sheet.defaultProps = {
  centerHeader: false,
  footerButtonList: [],
  hasCloseButton: true,
  hasFooter: true,
  hasRequiredFieldsIndicator: false,
  onClose: () => null,
  subtitle: "",
  testSection: "",
  warningTestSection: ""
};

export { Sheet as default };
